import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSEO from "../lib/useSEO";
import { Copy, ExternalLink, DollarSign, Users, Clock, CheckCircle2, RotateCcw, Wallet, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const STATUS_COLOR = {
    pending: "bg-amber-100 text-amber-900 border-amber-200",
    approved: "bg-blue-100 text-blue-900 border-blue-200",
    paid: "bg-emerald-100 text-emerald-900 border-emerald-200",
    reversed: "bg-muted text-muted-foreground border-border",
};

function StatCard({ label, value, sub, icon: Icon, accent }) {
    return (
        <div className="ll-card">
            <div className="flex items-start justify-between">
                <div>
                    <div className="ll-label">{label}</div>
                    <div className={`font-serif text-3xl font-semibold mt-1 tabular-nums ${accent || ""}`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
                </div>
                {Icon && <Icon className="h-5 w-5 text-primary opacity-60" />}
            </div>
        </div>
    );
}

function StatusPill({ ok, label }) {
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${ok
                ? "bg-emerald-100 text-emerald-900 border-emerald-200"
                : "bg-muted text-muted-foreground border-border"}`}
            data-testid={`connect-pill-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
            {ok ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
            {label}
        </span>
    );
}

function ConnectDiagnosticBanner({ diag }) {
    // Renders the structured Stripe error from the backend so the user can
    // read it on production where backend logs aren't accessible.
    if (!diag) return null;
    return (
        <div
            className="rounded-md border border-destructive/40 bg-destructive/10 p-3 mb-3 text-sm"
            data-testid="connect-diagnostic-banner"
        >
            <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                    <div>
                        <div className="font-semibold text-destructive" data-testid="connect-diag-headline">
                            {diag.headline || "Stripe rejected the request."}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Stage: <code className="font-mono">{diag.stage}</code>
                            {diag.error_type ? <> · Type: <code className="font-mono">{diag.error_type}</code></> : null}
                            {diag.stripe_request_id ? <> · Stripe req: <code className="font-mono" data-testid="connect-diag-request-id">{diag.stripe_request_id}</code></> : null}
                        </div>
                    </div>
                    {diag.hint && (
                        <div className="text-xs leading-relaxed" data-testid="connect-diag-hint">
                            <strong>What to do:</strong> {diag.hint}
                        </div>
                    )}
                    {diag.stripe_message && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw Stripe message</summary>
                            <pre className="mt-1 whitespace-pre-wrap break-words bg-card/60 border border-border rounded p-2 font-mono text-[11px]" data-testid="connect-diag-raw">{diag.stripe_message}</pre>
                        </details>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConnectPayoutCard({ connect, busy, onStart, onManage, onRefresh, lastError }) {
    // Three UI states:
    //   1. No Stripe account yet → "Connect with Stripe" button.
    //   2. Account exists but onboarding incomplete → "Continue with Stripe" + status pills.
    //   3. Fully onboarded (payouts_enabled + details_submitted) → green "Payouts active".
    const ready = !!connect?.payout_onboarding_complete;
    const hasAccount = !!connect?.has_account;
    const startedNotReady = hasAccount && !ready;
    const cardCls = ready
        ? "ll-card mb-6 border-emerald-200 bg-emerald-50/30"
        : "ll-card mb-6 border-amber-200 bg-amber-50/30";
    const headlineCls = ready ? "text-emerald-900" : "text-foreground";
    return (
        <div className={cardCls} data-testid="connect-payout-card">
            <div className="flex items-start gap-3">
                {ready
                    ? <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5" />
                    : <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5" />}
                <div className="flex-1">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                        <h3 className={`font-serif text-lg font-semibold ${headlineCls}`} data-testid="connect-headline">
                            {ready ? "Payouts active" : startedNotReady ? "Finish Stripe onboarding" : "Connect your Stripe account"}
                        </h3>
                        {hasAccount && (
                            <button onClick={onRefresh} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" title="Re-check status" data-testid="connect-refresh-btn">
                                <RotateCcw className="h-3 w-3" /> Refresh
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3" data-testid="connect-description">
                        {ready
                            ? "Approved commissions will be sent to your Stripe account on schedule. Your bank and tax info live in Stripe — we never store them."
                            : "Before approved commissions can pay out, complete secure onboarding with Stripe. Your bank account and tax info go directly to Stripe — never to our database. Stripe handles 1099 reporting automatically."}
                    </p>
                    <ConnectDiagnosticBanner diag={lastError} />
                    {hasAccount && (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="connect-status-pills">
                            <StatusPill ok={!!connect?.details_submitted} label="Details submitted" />
                            <StatusPill ok={!!connect?.charges_enabled} label="Charges enabled" />
                            <StatusPill ok={!!connect?.payouts_enabled} label="Payouts enabled" />
                        </div>
                    )}
                    {connect?.disabled_reason && (
                        <p className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="connect-disabled-reason">
                            Stripe is asking for more info: <strong>{connect.disabled_reason.replace(/_/g, " ")}</strong>. Continue onboarding to resolve.
                        </p>
                    )}
                    {connect?.error && !hasAccount && !lastError && (
                        <p className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="connect-error">
                            {connect.error}
                        </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {!ready && (
                            <button
                                onClick={onStart}
                                disabled={busy}
                                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                                data-testid="connect-start-btn"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                                {hasAccount ? "Continue with Stripe" : "Connect with Stripe"}
                            </button>
                        )}
                        {hasAccount && (
                            <button
                                onClick={onManage}
                                disabled={busy}
                                className="inline-flex items-center gap-2 rounded-full bg-card text-foreground border border-border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-60"
                                data-testid="connect-manage-btn"
                            >
                                <ExternalLink className="h-4 w-4" />
                                Manage in Stripe
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AffiliateDashboardPage() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // Stripe Connect onboarding state — fetched separately so the heavy
    // dashboard query stays snappy and Stripe outages don't blank the page.
    const [connect, setConnect] = useState(null);  // null | { has_account, charges_enabled, payouts_enabled, details_submitted, payout_onboarding_complete, ... }
    const [connectBusy, setConnectBusy] = useState(false);
    const [connectError, setConnectError] = useState(null);  // structured diagnostic from POST /affiliate/connect/onboard 502

    useSEO({ title: "Affiliate Dashboard", path: "/affiliate/dashboard", noindex: true });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/affiliate/dashboard");
            setData(data);
        } finally { setLoading(false); }
    };

    const loadConnectStatus = async () => {
        try {
            const { data } = await api.get("/affiliate/connect/status");
            setConnect(data);
        } catch (e) {
            // 503 (Stripe not configured) or 502 (Stripe error) → fall back to "not started".
            setConnect({ has_account: false, charges_enabled: false, payouts_enabled: false, details_submitted: false, payout_onboarding_complete: false, error: e?.response?.data?.detail });
        }
    };

    useEffect(() => { load(); /* Stripe Connect status check disabled while Connect UI is hidden. */ }, []);

    // Stripe Connect redirect handler kept defensive — if a stale link still
    // returns to us with ?connect=done|refresh, we just strip the param so the
    // URL stays clean. Toast/status pings disabled while the Connect UI is hidden.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const v = params.get("connect");
        if (!v) return;
        params.delete("connect");
        const qs = params.toString();
        window.history.replaceState({}, "", `${window.location.pathname}${qs ? "?" + qs : ""}`);
    }, []);

    const startConnectOnboarding = async () => {
        setConnectBusy(true);
        setConnectError(null);
        try {
            const { data } = await api.post("/affiliate/connect/onboard");
            if (data?.onboarding_url) {
                window.location.href = data.onboarding_url;
                return;  // browser will navigate; do not reset busy.
            }
            setConnectError({
                error_type: "no_url_returned",
                stage: "account_link",
                headline: "Stripe returned no onboarding URL.",
                hint: "Try clicking Connect with Stripe again. If this persists, the platform Stripe account profile likely needs more info.",
                stripe_message: "",
                stripe_request_id: null,
            });
            toast.error("Couldn't start Stripe onboarding. See details above.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.error_type) {
                // Structured diagnostic from backend — render in the banner.
                setConnectError(detail);
                // eslint-disable-next-line no-console
                console.error("[stripe-connect] onboard error", detail);
                toast.error(detail.headline || "Stripe Connect couldn't start.");
            } else {
                const msg = (typeof detail === "string" ? detail : null) || "Stripe Connect failed to start.";
                setConnectError({
                    error_type: "unknown",
                    stage: "unknown",
                    headline: msg,
                    hint: "Open browser DevTools → Network → click the failed POST /affiliate/connect/onboard request and copy the response.",
                    stripe_message: msg,
                    stripe_request_id: null,
                });
                toast.error(msg);
            }
        } finally {
            setConnectBusy(false);
        }
    };

    const openExpressDashboard = async () => {
        setConnectBusy(true);
        try {
            const { data } = await api.get("/affiliate/connect/login-link");
            if (data?.url) {
                window.open(data.url, "_blank", "noopener,noreferrer");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't open Stripe dashboard.");
        } finally {
            setConnectBusy(false);
        }
    };

    // While AuthProvider hydrates from localStorage, render a small loading
    // shell instead of bouncing through <Navigate>. Bouncing breaks the
    // ?connect=done/refresh return-from-Stripe redirect because the query
    // param gets stripped by the redirect chain.
    if (authLoading) {
        return (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-sm text-muted-foreground" data-testid="affiliate-dashboard-loading">Loading…</div>
            </section>
        );
    }
    if (!user) return <Navigate to="/affiliate/auth" replace />;
    if (user.role !== "affiliate") return <Navigate to="/" replace />;

    if (!data) {
        return (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-sm text-muted-foreground">Loading…</div>
            </section>
        );
    }

    const { profile, stats, commissions, referred_attorneys } = data;
    const refLink = `${window.location.origin}/join?ref=${profile.referral_code}`;

    const copy = (text, label) => {
        navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
    };

    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="affiliate-dashboard">
            <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
                <div>
                    <h1 className="font-serif text-3xl font-bold">Welcome, {profile.name}</h1>
                    <p className="text-sm text-muted-foreground">Affiliate status: <span className="font-semibold capitalize">{profile.affiliate_status}</span></p>
                </div>
            </div>

            {/* REFERRAL CODE + LINK */}
            <div className="ll-card mb-6">
                <h2 className="font-serif text-lg font-semibold mb-3">Your referral code</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <div className="ll-label mb-1">Code</div>
                        <div className="flex items-center gap-2">
                            <code className="font-mono text-2xl font-bold tracking-wider text-primary px-3 py-2 rounded bg-primary/5 flex-1" data-testid="referral-code">{profile.referral_code}</code>
                            <button onClick={() => copy(profile.referral_code, "Code")} className="rounded-full p-2 hover:bg-muted" title="Copy code" data-testid="copy-code-btn">
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className="ll-label mb-1">Referral link</div>
                        <div className="flex items-center gap-2">
                            <input readOnly className="ll-input font-mono text-xs flex-1" value={refLink} data-testid="referral-link" />
                            <button onClick={() => copy(refLink, "Link")} className="rounded-full p-2 hover:bg-muted" title="Copy link" data-testid="copy-link-btn">
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                    Share this link or code. When an attorney signs up through it and pays for any plan, you start earning monthly commissions.
                </p>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard label="Referred" value={stats.referred_count} sub={`${stats.active_subscriptions} active`} icon={Users} />
                <StatCard label="Pending" value={`$${stats.totals.pending.amount.toLocaleString()}`} sub={`${stats.totals.pending.count} commissions · ${stats.hold_days}d hold`} icon={Clock} accent="text-amber-700" />
                <StatCard label="Approved" value={`$${stats.totals.approved.amount.toLocaleString()}`} sub={`${stats.totals.approved.count} ready for payout`} icon={CheckCircle2} accent="text-primary" />
                <StatCard label="Paid (lifetime)" value={`$${stats.totals.paid.amount.toLocaleString()}`} sub={`${stats.totals.paid.count} payouts`} icon={DollarSign} accent="text-emerald-700" />
            </div>

            {/* MANUAL PAYOUT STATUS NOTICE — visible above Connect onboarding so affiliates know what to expect */}
            <div className="rounded-md border border-amber-200 bg-amber-50/40 px-4 py-3 mb-6 text-sm" data-testid="manual-payout-notice">
                <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <div>
                        <strong>Manual payouts currently active.</strong> Automatic Stripe payouts will be enabled after verification is complete.
                        Your referrals, commissions, and balances continue to track here in real time.
                    </div>
                </div>
            </div>

            {/* PAYOUT ONBOARDING — Stripe Connect Express temporarily hidden.
                The component + backing endpoints are kept in place so we can
                re-enable when Stripe Connect verification is complete. */}
            {false && (
                <ConnectPayoutCard
                    connect={connect}
                    busy={connectBusy}
                    lastError={connectError}
                    onStart={startConnectOnboarding}
                    onManage={openExpressDashboard}
                    onRefresh={loadConnectStatus}
                />
            )}
            {/* REFERRED ATTORNEYS */}
            <div className="ll-card mb-6">
                <h2 className="font-serif text-lg font-semibold mb-3">Referred attorneys ({stats.referred_count})</h2>
                {referred_attorneys.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="no-referrals">No attorneys yet. Share your link to start earning.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="ll-label py-2 pr-4">Attorney</th>
                                    <th className="ll-label py-2 pr-4">Email</th>
                                    <th className="ll-label py-2 pr-4">Signed up</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referred_attorneys.map((a) => (
                                    <tr key={a.id} className="border-b border-border last:border-0" data-testid={`referred-${a.id}`}>
                                        <td className="py-2 pr-4 font-medium">{a.name}</td>
                                        <td className="py-2 pr-4 text-muted-foreground">{a.email}</td>
                                        <td className="py-2 pr-4 text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* COMMISSION LEDGER */}
            <div className="ll-card">
                <h2 className="font-serif text-lg font-semibold mb-3">Commission history</h2>
                {commissions.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="no-commissions">No commissions yet. They'll appear here when your referred attorneys pay.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="ll-label py-2 pr-4">Date</th>
                                    <th className="ll-label py-2 pr-4">Attorney</th>
                                    <th className="ll-label py-2 pr-4">Tier</th>
                                    <th className="ll-label py-2 pr-4 text-right">Amount</th>
                                    <th className="ll-label py-2 pr-4">Status</th>
                                    <th className="ll-label py-2">Hold / paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commissions.map((c) => (
                                    <tr key={c.id} className="border-b border-border last:border-0" data-testid={`commission-${c.id}`}>
                                        <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</td>
                                        <td className="py-2 pr-4 font-medium">{c.attorney_name || "—"}</td>
                                        <td className="py-2 pr-4 text-muted-foreground capitalize">{c.tier === "top_spot" ? "Elite" : c.tier === "featured" ? "Pro" : "Basic"}</td>
                                        <td className="py-2 pr-4 text-right font-semibold tabular-nums">${c.amount.toFixed(2)}</td>
                                        <td className="py-2 pr-4">
                                            <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[c.status] || ""}`}>{c.status}</span>
                                        </td>
                                        <td className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                                            {c.status === "pending" && c.hold_until && <>until {new Date(c.hold_until).toLocaleDateString()}</>}
                                            {c.status === "paid" && c.paid_at && <>paid {new Date(c.paid_at).toLocaleDateString()}</>}
                                            {c.status === "reversed" && c.reversed_at && <>reversed {new Date(c.reversed_at).toLocaleDateString()}</>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
}
